import { defineStore } from "pinia";
import { markRaw, Raw } from "vue";
import { getCurrentUser, fetchAuthSession, fetchUserAttributes, updateUserAttributes } from "aws-amplify/auth";

import posthog from "posthog-js";

import {
	tiers,
	listedTiers,
	catalogEntryForTier,
	groupCode,
	CognitoAccountGroup,
	AccountGroupCode,
	ResolvedTierDetail,
	UserLimitsE,
	resolveTierDetail,
	allowanceFromTierLimits,
	allowanceFromUserRecord,
	MAPPED_USER_RECORD_ALLOWANCE_KEYS,
} from "@/js/tierconfig";
import { SERVED_CORE_CLASSES } from "@/types/api/cores";
import * as signaloidClient from "@/js/signaloidClient";
import { sdk } from "@/js/sdk";
import { SdkError, ERROR_CODES } from "@signaloid/cloud-compute-engine-client";
import {
	AccountLogsResponse,
	AWSAuthenticatedSession,
	AWSAuthenticatedUserObject,
	CurrentUser,
	User,
	OnboardingState,
	UserCustomization,
	UserPreferences,
} from "@/types/user";

import { SubscriptionConfig } from "@/types/api/subscriptions";
import { TierCatalogEntry } from "@/types/api/tiers";
import { monitoringCaptureError, monitoringTagBootSource } from "@/plugins/monitoring";
import { isTransientError, sleep } from "@/js/utilities";
import { pushFeedback } from "@/composables/feedbackQueue";
import { readCachedRecord, removeCachedRecord, setListCacheScope, writeCachedRecord } from "@/js/listCache";
import { pickKeys, WriteGenerations } from "@/js/optimisticWrites";

const TIER_RESOLUTION_TRANSACTION = "Resolve current user tier details";
const USER_RECORD_TRANSACTION = "Fetch user object from DB";
const SUBSCRIPTION_CACHE_KEY = "subscription";
const USER_RECORD_CACHE_KEY = "userRecord";
const MONTHLY_QUOTA_USAGE_CACHE_KEY = "monthlyQuotaUsage";

const PREFERENCE_SETTING_NAMES: Record<keyof UserPreferences, string> = {
	Editor_Theme: "editor theme",
	Editor_Layout: "editor layout",
	Editor_Layout_VariableViewer: "Variable Viewer visibility",
	Editor_Execution_Core: "execution core",
	Editor_Execution_Arguments: "program arguments",
	Editor_Compilation_Flags: "compilation flags",
	Editor_Execution_DataSources: "data sources",
	Editor_Execution_CodeLanguage: "code language",
	Editor_SourceCode: "code",
	Execution_DefaultCore: "default core",
	Execution_DefaultReferenceCore: "default reference core",
	Execution_DefaultDataSources: "default data sources",
	UpdatedAt: "settings",
};

export interface PreferenceWriteOptions {
	// Set only for a change the user made, so background writes fail silently.
	shouldReportFailure?: boolean;
}

let preferenceConfirmationCount = 0;

function unresolvedTierMessage(cause: "static tier" | "catalog entry", tierCode: string, servedTier: string): string {
	return `Tier details unresolved: no ${cause} for code "${tierCode}", served as "${servedTier}"`;
}

function isCachedSubscription(record: unknown): record is SubscriptionConfig {
	if (typeof record !== "object" || record === null) {
		return false;
	}
	const tier: unknown = (record as { Tier?: unknown }).Tier;
	return tier === undefined || typeof tier === "string";
}

function isCachedUserRecord(record: unknown): record is User {
	return typeof record === "object" && record !== null && typeof (record as { UserID?: unknown }).UserID === "string";
}

function isCachedMonthlyQuotaRow(row: unknown): row is MonthlyQuotaRow {
	if (typeof row !== "object" || row === null) {
		return false;
	}
	const { id, label, used, limit, unlimited, pct } = row as Record<string, unknown>;
	return (
		typeof id === "string" &&
		typeof label === "string" &&
		typeof used === "number" &&
		(limit === undefined || limit === null || typeof limit === "number") &&
		typeof unlimited === "boolean" &&
		typeof pct === "number"
	);
}

function isCachedMonthlyQuotaUsage(record: unknown): record is MonthlyQuotaUsage {
	if (typeof record !== "object" || record === null) {
		return false;
	}
	const { rows, resetsAt } = record as { rows?: unknown; resetsAt?: unknown };
	return (
		Array.isArray(rows) &&
		rows.every(isCachedMonthlyQuotaRow) &&
		(resetsAt === undefined || typeof resetsAt === "number")
	);
}

// Federated sign-ins can land before custom:user_id is set. The Cognito sub always identifies them.
function scopeListCacheToUser(userID: string | undefined, cognitoSub: string | undefined): void {
	const listCacheScope = userID ?? cognitoSub;
	if (listCacheScope) {
		setListCacheScope(listCacheScope);
	}
}

async function getSubscriptionRetryingTransientErrors() {
	for (const backoffMs of [500, 1000]) {
		try {
			return await sdk.subscriptions.get();
		} catch (error) {
			if (!isTransientError(error)) {
				throw error;
			}
			await sleep(backoffMs);
		}
	}
	return sdk.subscriptions.get();
}

export type MonthlyQuotaRow = {
	id: string;
	label: string;
	used: number;
	limit: number | null | undefined;
	unlimited: boolean;
	pct: number;
};

export type MonthlyQuotaUsage = { rows: MonthlyQuotaRow[]; resetsAt?: number };

type State = {
	currentAuthenticatedUserObject: undefined | null | AWSAuthenticatedUserObject;
	currentAuthenticatedUserIsSeeded: boolean;
	currentAuthenticatedUserFetchInFlight: undefined | Promise<Awaited<ReturnType<typeof getCurrentUser>> | null>;
	currentUserObjectFromDB: undefined | User;
	cachedCurrentUserObjectFromDB: undefined | User;
	cachedUserRecordRead: undefined | Promise<void>;
	cachedMonthlyQuotaUsage: undefined | MonthlyQuotaUsage;
	currentUserObjectFetchInFlight: undefined | Promise<void>;
	currentUserSessionObject: undefined | null | AWSAuthenticatedSession;
	currentUserCustomizations: undefined | null | UserCustomization;
	currentUserTierCode: undefined | "" | AccountGroupCode;
	currentUserTierDetails: undefined | ResolvedTierDetail;
	currentUserSubscriptions: undefined | SubscriptionConfig;
	tierCatalog: undefined | TierCatalogEntry[];
	tierCatalogFetchInFlight: undefined | Promise<void>;
	tierResolutionFailed: boolean;
	reportedProblems: string[];
	currentUserActivityLogs: undefined | AccountLogsResponse;
	activityLogsLoading: boolean;
	activityLogsError: string | null;
	pendingPreferences: Partial<UserPreferences>;
	preferenceWriteGenerations: Raw<WriteGenerations<keyof UserPreferences>>;
	preferenceConfirmations: Raw<Map<keyof UserPreferences, number>>;
	preferenceWriteQueue: Promise<unknown>;
};

export const useUserStore = defineStore("user", {
	state: (): State => ({
		currentAuthenticatedUserObject: undefined,
		currentAuthenticatedUserIsSeeded: false,
		currentAuthenticatedUserFetchInFlight: undefined,
		currentUserObjectFromDB: undefined,
		cachedCurrentUserObjectFromDB: undefined,
		cachedUserRecordRead: undefined,
		cachedMonthlyQuotaUsage: undefined,
		currentUserObjectFetchInFlight: undefined,
		currentUserSessionObject: undefined,
		currentUserCustomizations: undefined,
		currentUserTierCode: undefined,
		currentUserTierDetails: undefined,
		currentUserSubscriptions: undefined,
		tierCatalog: undefined,
		tierCatalogFetchInFlight: undefined,
		tierResolutionFailed: false,
		reportedProblems: [],
		currentUserActivityLogs: undefined,
		activityLogsLoading: false,
		activityLogsError: null,
		pendingPreferences: {},
		preferenceWriteGenerations: markRaw(new WriteGenerations<keyof UserPreferences>()),
		preferenceConfirmations: markRaw(new Map<keyof UserPreferences, number>()),
		preferenceWriteQueue: Promise.resolve(),
	}),
	getters: {
		currentUser(state): undefined | CurrentUser {
			//! deprecated
			if (!state.currentAuthenticatedUserObject) {
				return undefined;
			}
			const attributes = state.currentAuthenticatedUserObject?.attributes;
			return {
				email: attributes?.email ?? "",
				name: attributes?.name ?? "",
				affiliation: attributes?.["custom:affiliation"] ?? "",
				userID: attributes?.["custom:user_id"] ?? "",
				avatar: "",
			};
		},
		currentUserResourceUsage(state) {
			return state.currentUserObjectFromDB?.ResourceUsage;
		},
		preferences(state): Partial<UserPreferences> | undefined {
			const confirmedPreferences = state.currentUserObjectFromDB?.Preferences;
			return confirmedPreferences && { ...confirmedPreferences, ...state.pendingPreferences };
		},
		// Per-quota usage for the monthly-resetting limits, shared by the home Quotas
		// tile and the "Upgrade Account" quick action. Returns [] while the tier
		// hasn't loaded so callers don't briefly read every quota as "unlimited".
		monthlyQuotaUsage(state): MonthlyQuotaRow[] {
			const usage = state.currentUserObjectFromDB?.ResourceUsage;
			if (!usage) return [];
			const allowance = state.currentUserTierDetails?.allowance;
			if (!allowance) return [];
			const defs: { id: string; label: string }[] = [
				{ id: UserLimitsE.ExecutionTimeInMillisecondsCount, label: "Task Execution Time" },
				{ id: UserLimitsE.APIAccessCount, label: "API Requests" },
				{ id: UserLimitsE.PlotCount, label: "Distribution Plots" },
			];
			const usageMap = usage as unknown as Record<string, number | undefined>;
			const allowanceMap = allowance as unknown as Record<string, number | null | undefined>;
			return defs.map((d) => {
				const used = Number(usageMap[d.id] ?? 0);
				const limit = allowanceMap[d.id];
				const unlimited = limit === undefined;
				// `null` allowance means "not allowed" per tierconfig contract; treat
				// the same as a fully-consumed quota so alerts and upgrade CTA fire.
				const blocked = limit === null || limit === 0;
				const pct = unlimited ? 0 : blocked ? 1 : Math.min(1, used / Number(limit));
				return { id: d.id, label: d.label, used, limit, unlimited, pct };
			});
		},
		freshOrCachedMonthlyQuotaUsage(): MonthlyQuotaUsage {
			if (this.monthlyQuotaUsage.length > 0) {
				return { rows: this.monthlyQuotaUsage, resetsAt: this.currentUserResourceUsage?.ResetsAt };
			}
			return this.cachedMonthlyQuotaUsage ?? { rows: [] };
		},
		tierCatalogLoadFailed(state): boolean {
			return state.tierCatalog?.length === 0;
		},
		tierDetails(state): ResolvedTierDetail[] {
			return listedTiers.flatMap((tier) => {
				const catalogEntry = catalogEntryForTier(state.tierCatalog, tier.code);
				if (catalogEntry === undefined) {
					return [];
				}

				return [resolveTierDetail(tier, allowanceFromTierLimits(catalogEntry.Limits, SERVED_CORE_CLASSES))];
			});
		},
		currentUserPrimaryAtomicNetwork: (state) => {
			if (!state.currentUserCustomizations?.AtomicNetworks) {
				return "default";
			}

			return state.currentUserCustomizations?.AtomicNetworks?.length > 0
				? state.currentUserCustomizations?.AtomicNetworks[0]
				: "default";
		},
		currentUserPrimaryOrganization: (state): string | null => {
			if (!state.currentUserCustomizations?.Organizations) {
				return null;
			}
			return state.currentUserCustomizations?.Organizations?.length > 0
				? state.currentUserCustomizations?.Organizations[0]
				: null;
		},
	},
	actions: {
		reportProblemOnce(message: string, transactionName: string) {
			if (this.reportedProblems.includes(message)) {
				return;
			}

			this.reportedProblems.push(message);
			monitoringCaptureError(new Error(message), transactionName);
		},

		reportUserRecordShape(record: User) {
			const unknownAllowanceKeys = Object.keys(record.Allowance).filter(
				(key) => !MAPPED_USER_RECORD_ALLOWANCE_KEYS.has(key),
			);
			if (unknownAllowanceKeys.length > 0) {
				this.reportProblemOnce(
					`User record served unknown allowance keys: ${unknownAllowanceKeys.join(", ")}`,
					USER_RECORD_TRANSACTION,
				);
			}
		},

		async refreshCurrentUserSession() {
			const newSession = await fetchAuthSession({ forceRefresh: true });
			this.currentUserSessionObject = newSession;
			await this.registerCurrentUserTokens();

			await this.fetchCurrentUserFromAWS({ isAfterWrite: true });

			return this.currentUserSessionObject;
		},

		async registerCurrentUserTokens() {
			// This can be streamlined to
			signaloidClient.registerTokens(
				fetchAuthSession()
					.then((session) => session.tokens?.accessToken?.toString() ?? "")
					.catch((e) => {
						console.log("registerCurrentUserTokens - getAccessToken", e);
						return "";
					}),
				fetchAuthSession()
					.then((session) => session.tokens?.idToken?.toString() ?? "")
					.catch((e) => {
						console.log("registerCurrentUserTokens - getIdToken", e);
						return "";
					}),
			);

			signaloidClient.registerRefreshTokenCallbacks(
				async () => {
					const session = await fetchAuthSession({ forceRefresh: true });
					return session.tokens?.accessToken;
				},
				async () => {
					const session = await fetchAuthSession({ forceRefresh: true });
					return session.tokens?.idToken;
				},
			);
		},

		/*
		 * ======================================================
		 * Async functions for getting user data with dependencies
		 */

		async getCurrentAuthenticatedUserObject() {
			if (this.currentAuthenticatedUserObject === undefined) {
				await this.fetchCurrentUserFromAWS();
			}
			return this.currentAuthenticatedUserObject;
		},

		async getCurrentUserID() {
			if (this.currentAuthenticatedUserObject === undefined) {
				this.getCurrentAuthenticatedUserObject();
			}
			return new Promise<string>((resolve) => {
				const loop = () =>
					this.currentAuthenticatedUserObject === null ||
					this.currentAuthenticatedUserObject?.attributes !== undefined
						? resolve(this.currentAuthenticatedUserObject?.attributes?.["custom:user_id"] ?? "")
						: setTimeout(loop);
				loop();
			});
		},

		async getCurrentUserOrganizationID() {
			if (this.currentAuthenticatedUserObject === undefined) {
				await this.getCurrentAuthenticatedUserObject();
			}
			return this.currentAuthenticatedUserObject?.attributes?.["custom:organization_id"] ?? null;
		},
		async createOrganization(data: { Name: string; DedicatedInstanceName?: string }) {
			try {
				const response = await sdk.organizations.create(data);

				await this.refreshCurrentUserSession();

				return response;
			} catch (error) {
				monitoringCaptureError(error, "Create organization");
				console.log("Error creating organization :>> ", error);
				throw error;
			}
		},

		async getCurrentUserSubscriptions() {
			if (this.currentUserSubscriptions === undefined) {
				await this.fetchCurrentUserSubscriptions();
			}

			return new Promise<null | SubscriptionConfig>((resolve) => {
				const loop = () =>
					this.currentUserSubscriptions !== undefined
						? resolve(this.currentUserSubscriptions)
						: setTimeout(loop);
				loop();
			});
		},
		async fetchTierCatalog() {
			if (this.tierCatalog !== undefined && this.tierCatalog.length > 0) {
				return;
			}

			if (this.tierCatalogFetchInFlight !== undefined) {
				return this.tierCatalogFetchInFlight;
			}

			this.tierCatalogFetchInFlight = (async () => {
				try {
					this.tierCatalog = await signaloidClient.getTiers();
				} catch (error) {
					this.tierCatalog = [];
					monitoringCaptureError(error, "Fetch tier catalog");
				} finally {
					this.tierCatalogFetchInFlight = undefined;
				}
			})();

			return this.tierCatalogFetchInFlight;
		},

		async getCurrentUserTierDetails() {
			let tierCode;
			try {
				tierCode = await this.getCurrentUserTierCode();
			} catch {
				this.currentUserTierDetails = undefined;
				this.tierResolutionFailed = true;
				return undefined;
			}

			const staticTierDetails = tiers.find((e) => e.code === tierCode);

			await this.fetchTierCatalog();
			const catalogEntry = catalogEntryForTier(this.tierCatalog, tierCode);

			const servedTier = this.currentUserSubscriptions?.Tier;
			const accountCarriesATier = servedTier !== undefined && servedTier !== "NO_TIER";

			if (accountCarriesATier && staticTierDetails === undefined) {
				this.reportProblemOnce(
					unresolvedTierMessage("static tier", tierCode, servedTier),
					TIER_RESOLUTION_TRANSACTION,
				);
			}
			if (accountCarriesATier && catalogEntry === undefined && !this.tierCatalogLoadFailed) {
				this.reportProblemOnce(
					unresolvedTierMessage("catalog entry", tierCode, servedTier),
					TIER_RESOLUTION_TRANSACTION,
				);
			}

			if (staticTierDetails === undefined) {
				this.currentUserTierDetails = undefined;
				this.tierResolutionFailed = false;
				return undefined;
			}

			const isSignedIn =
				this.currentAuthenticatedUserObject !== undefined && this.currentAuthenticatedUserObject !== null;
			if (this.currentUserObjectFromDB === undefined && isSignedIn) {
				await this.fetchCurrentUserObjectFromDB().catch(() => undefined);
			}

			const userRecord = this.currentUserObjectFromDB;
			const allowance = userRecord
				? allowanceFromUserRecord(
						userRecord.Allowance,
						userRecord.CoreClasses.map((coreClass) => coreClass.Name),
						userRecord.Microarchitectures.map((microArchitecture) => microArchitecture.Name),
					)
				: catalogEntry && allowanceFromTierLimits(catalogEntry.Limits, SERVED_CORE_CLASSES);

			if (allowance === undefined) {
				this.currentUserTierDetails = undefined;
				this.tierResolutionFailed = true;
				return undefined;
			}

			this.currentUserTierDetails = resolveTierDetail(staticTierDetails, allowance);
			this.tierResolutionFailed = false;
			this.saveMonthlyQuotaUsage();

			return this.currentUserTierDetails;
		},

		async getCurrentUserTierCode() {
			if (this.currentUserSubscriptions === undefined) {
				await this.fetchCurrentUserSubscriptions();
			}

			this.currentUserTierCode = groupCode(this.currentUserSubscriptions?.Tier);

			return this.currentUserTierCode;
		},

		async getCurrentUserGroups() {
			if (this.currentUserSessionObject === undefined) {
				this.fetchCurrentUserSessionFromAWS();
			}
			return new Promise<null | CognitoAccountGroup[]>((resolve) => {
				const loop = () => {
					if (this.currentUserSessionObject !== undefined) {
						if (this.currentUserSessionObject === null) {
							return resolve(null);
						} else {
							resolve(
								(this.currentUserSessionObject as any).tokens?.accessToken?.payload?.[
									"cognito:groups"
								] ?? null,
							);
						}
					} else {
						setTimeout(loop);
					}
				};
				loop();
			});
		},
		async getCurrentUserPreferences(): Promise<Partial<UserPreferences> | undefined> {
			if (this.currentUserObjectFromDB === undefined) {
				await this.fetchCurrentUserObjectFromDB().catch(() => undefined);
			}

			return this.preferences;
		},
		async getCurrentUserOnboardingState(): Promise<Partial<OnboardingState> | undefined> {
			if (this.currentUserObjectFromDB === undefined) {
				await this.fetchCurrentUserObjectFromDB();
			}

			return this.currentUserObjectFromDB?.OnboardingState;
		},
		async getCurrentUserCustomizations(): Promise<null | Partial<UserCustomization>> {
			if (this.currentUserCustomizations === undefined) {
				await this.fetchCurrentUserCustomizationsFromDB().catch(() => undefined);
			}

			return this.currentUserCustomizations ?? null;
		},
		async getCurrentUserPrimaryOrganization() {
			if (this.currentUserCustomizations === undefined) {
				this.fetchCurrentUserCustomizationsFromDB();
			}
			return new Promise<null | string>((resolve) => {
				const loop = () =>
					this.currentUserCustomizations !== undefined
						? resolve(this.currentUserPrimaryOrganization)
						: setTimeout(loop);
				loop();
			});
		},
		async getCurrentUserPrimaryAtomicNetwork() {
			if (this.currentUserCustomizations === undefined) {
				this.fetchCurrentUserCustomizationsFromDB();
			}
			return new Promise<null | string>((resolve) => {
				const loop = () =>
					this.currentUserCustomizations !== undefined
						? resolve(this.currentUserPrimaryAtomicNetwork)
						: setTimeout(loop);
				loop();
			});
		},

		/*
		 * ======================================================
		 * Functions for fetching user data from remote
		 */

		async fetchCurrentUserFromAWS({ isAfterWrite = false }: { isAfterWrite?: boolean } = {}) {
			const pendingFetch = this.currentAuthenticatedUserFetchInFlight;
			if (pendingFetch !== undefined) {
				if (!isAfterWrite) {
					return pendingFetch;
				}
				// The pending fetch started before the write, any fetch in flight after it settles did not.
				await pendingFetch;
				return this.fetchCurrentUserFromAWS();
			}

			this.currentAuthenticatedUserFetchInFlight = (async () => {
				try {
					// Force bypass cache to get fresh user data from Cognito
					const user = await getCurrentUser();
					const attributes = await fetchUserAttributes();
					this.currentAuthenticatedUserObject = { ...user, attributes: attributes as Record<string, string> };
					this.currentAuthenticatedUserIsSeeded = false;
					scopeListCacheToUser(attributes["custom:user_id"], user.userId);
					return user;
				} catch (error) {
					const reportedError = error instanceof Error ? error : new Error(String(error));
					if (reportedError.name !== "UserUnAuthenticatedException") {
						monitoringCaptureError(reportedError, "Fetch current user from AWS");
					}
					if (this.currentAuthenticatedUserIsSeeded || this.currentAuthenticatedUserObject === undefined) {
						this.currentAuthenticatedUserObject = null;
					}
					return null;
				}
			})().finally(() => {
				this.currentAuthenticatedUserFetchInFlight = undefined;
			});

			return this.currentAuthenticatedUserFetchInFlight;
		},
		async seedCurrentUserFromIdToken(idTokenPayload: Record<string, unknown>): Promise<void> {
			const claims: Record<string, string> = Object.fromEntries(
				Object.entries(idTokenPayload).filter(
					(claim): claim is [string, string] => typeof claim[1] === "string",
				),
			);
			scopeListCacheToUser(claims["custom:user_id"], claims.sub);
			if (this.currentAuthenticatedUserObject === undefined) {
				this.currentAuthenticatedUserObject = {
					username: claims["cognito:username"],
					userId: claims.sub,
					// Without the claim, getCurrentUserID must wait for fetchUserAttributes rather than resolve "".
					...(claims["custom:user_id"] !== undefined && { attributes: claims }),
				};
				this.currentAuthenticatedUserIsSeeded = true;
			}

			this.cachedUserRecordRead = readCachedRecord(USER_RECORD_CACHE_KEY, isCachedUserRecord).then(
				(cachedUserRecord) => {
					this.cachedCurrentUserObjectFromDB = cachedUserRecord;
				},
			);
			await Promise.all([
				readCachedRecord(SUBSCRIPTION_CACHE_KEY, isCachedSubscription).then((cachedSubscription) => {
					const drewFromCache =
						this.currentUserSubscriptions === undefined && cachedSubscription !== undefined;
					this.currentUserSubscriptions ??= cachedSubscription;
					monitoringTagBootSource(drewFromCache ? "cache" : "network");
				}),
				this.cachedUserRecordRead,
				readCachedRecord(MONTHLY_QUOTA_USAGE_CACHE_KEY, isCachedMonthlyQuotaUsage).then((cachedUsage) => {
					this.cachedMonthlyQuotaUsage = cachedUsage;
				}),
			]);
		},
		saveMonthlyQuotaUsage(): void {
			if (this.monthlyQuotaUsage.length > 0) {
				writeCachedRecord(MONTHLY_QUOTA_USAGE_CACHE_KEY, this.freshOrCachedMonthlyQuotaUsage);
			}
		},
		async fetchCurrentUserCustomizationsFromDB() {
			try {
				const currentUserID = await this.getCurrentUserID();
				// if for some reason currentUserID is still not available
				if (!currentUserID) {
					throw new Error("User ID not found");
				}

				const response = await sdk.users.getCustomization(currentUserID);
				this.currentUserCustomizations = response;

				if (this.currentUserCustomizations.Organizations) {
					posthog?.setPersonProperties({
						organizations: this.currentUserCustomizations.Organizations,
					});
				}

				if (this.currentUserCustomizations.AtomicNetworks) {
					posthog?.setPersonProperties({
						atomicNetworks: this.currentUserCustomizations.AtomicNetworks,
					});
				}
			} catch (error) {
				this.currentUserCustomizations = null;

				if (error instanceof SdkError) {
					if (error.code !== ERROR_CODES.API_NOT_FOUND) {
						monitoringCaptureError(error, "Fetch user customisations from DB");
					}
					return;
				}
				monitoringCaptureError(error, "Fetch user customisations from DB");
			}
		},

		async fetchCurrentUserObjectFromDB() {
			if (this.currentUserObjectFetchInFlight !== undefined) {
				return this.currentUserObjectFetchInFlight;
			}

			this.currentUserObjectFetchInFlight = (async () => {
				try {
					const currentUserID = await this.getCurrentUserID();
					if (!currentUserID) {
						throw new Error("User ID not found");
					}

					const confirmationCountAtRequest = preferenceConfirmationCount;
					const response = await sdk.users.getOne(currentUserID);
					const keysConfirmedSince = [...this.preferenceConfirmations]
						.filter(([, confirmation]) => confirmation > confirmationCountAtRequest)
						.map(([key]) => key);
					this.currentUserObjectFromDB =
						keysConfirmedSince.length === 0
							? response
							: {
									...response,
									Preferences: {
										...response.Preferences,
										...pickKeys(
											this.currentUserObjectFromDB?.Preferences ?? {},
											keysConfirmedSince,
										),
									},
								};
					writeCachedRecord(USER_RECORD_CACHE_KEY, this.currentUserObjectFromDB);
					this.saveMonthlyQuotaUsage();
					this.reportUserRecordShape(response);
				} catch (error) {
					monitoringCaptureError(error, USER_RECORD_TRANSACTION);
					console.log("Error fetching user data from DB :>> ", error);
					throw error;
				} finally {
					this.currentUserObjectFetchInFlight = undefined;
				}
			})();

			return this.currentUserObjectFetchInFlight;
		},
		async fetchCurrentUserSubscriptions() {
			try {
				const [currentUserID, response] = await Promise.all([
					this.getCurrentUserID(),
					getSubscriptionRetryingTransientErrors(),
				]);
				// if for some reason currentUserID is still not available
				if (!currentUserID) {
					throw new Error("User ID not found");
				}

				this.currentUserSubscriptions = response;
				// A cached tierless plan would draw the onboarding shell on the reload that follows onboarding.
				if (response.Tier !== undefined && response.Tier !== "NO_TIER") {
					writeCachedRecord(SUBSCRIPTION_CACHE_KEY, response);
				} else {
					removeCachedRecord(SUBSCRIPTION_CACHE_KEY);
				}
			} catch (error) {
				/*
				 *	We only catch this here to capture it in monitoring. We will
				 *	not handle this exception here.
				 */
				monitoringCaptureError(error, "Fetch user subscription");
				console.log("Error fetching user subscriptions :>> ", error);
				// re-throw error
				throw error;
			}
		},
		async fetchCurrentUserSessionFromAWS(): Promise<AWSAuthenticatedSession | null> {
			try {
				const session = await fetchAuthSession();
				this.currentUserSessionObject = session;
				return session;
			} catch {
				// User is not logged in - this is an expected state, not an error
				// Return null to indicate no session exists
				this.currentUserSessionObject = null;
				return null;
			}
		},
		async updateUserName(name: string): Promise<void> {
			await getCurrentUser();
			await updateUserAttributes({
				userAttributes: {
					name: name,
				},
			});
			await this.fetchCurrentUserFromAWS({ isAfterWrite: true });
		},

		/*
		 *	Local-first: the new values show now on top of the confirmed record, and the
		 *	PATCHes go out one at a time. Resolves once this write settles, false if it failed.
		 */
		async syncUserPreferenceWithRemote(
			newPreferences: Partial<UserPreferences>,
			{ shouldReportFailure = false }: PreferenceWriteOptions = {},
		): Promise<boolean> {
			const updatedKeys = Object.keys(newPreferences) as (keyof UserPreferences)[];
			const writeGenerations = this.preferenceWriteGenerations.claim(updatedKeys);
			const currentUserIDLookup = this.getCurrentUserID();
			Object.assign(this.pendingPreferences, newPreferences);

			// Keys a newer write has claimed since stay in view as that write's values.
			const ownedKeys = () =>
				updatedKeys.filter((key) => this.preferenceWriteGenerations.owns(key, writeGenerations));
			const write = this.preferenceWriteQueue.then(async () => {
				const sentKeys = ownedKeys();
				if (sentKeys.length === 0) {
					return true;
				}
				const sentPreferences = pickKeys(newPreferences, sentKeys);
				try {
					const currentUserID = await currentUserIDLookup;
					if (!currentUserID) {
						throw new Error("User ID not found");
					}
					const newUserPreferencesData = await sdk.users.update(currentUserID, {
						Preferences: sentPreferences,
					});
					const remotePreferences = (newUserPreferencesData.Preferences ?? {}) as Partial<UserPreferences>;
					// Only keys the response actually carries: an omitted one must not blank the local value.
					const echoedKeys = sentKeys.filter((key) => key in remotePreferences);
					preferenceConfirmationCount += 1;
					for (const key of sentKeys) {
						this.preferenceConfirmations.set(key, preferenceConfirmationCount);
					}
					if (this.currentUserObjectFromDB) {
						this.currentUserObjectFromDB.Preferences = {
							...this.currentUserObjectFromDB.Preferences,
							...sentPreferences,
							...pickKeys(remotePreferences, echoedKeys),
						};
						writeCachedRecord(USER_RECORD_CACHE_KEY, this.currentUserObjectFromDB);
					}
					return true;
				} catch (error) {
					monitoringCaptureError(error, "Sync user preferences with DB");
					const [firstOwnedKey] = ownedKeys();
					if (shouldReportFailure && firstOwnedKey !== undefined) {
						pushFeedback(
							`Could not save your ${PREFERENCE_SETTING_NAMES[firstOwnedKey]}. Please try again.`,
							"error",
						);
					}
					return false;
				} finally {
					for (const key of ownedKeys()) {
						delete this.pendingPreferences[key];
					}
				}
			});
			this.preferenceWriteQueue = write;
			return write;
		},
		async syncOnboardingStateWithRemote(newState: Partial<OnboardingState>): Promise<boolean> {
			try {
				const currentUserID = await this.getCurrentUserID();
				if (!currentUserID) {
					throw new Error("User ID not found");
				}
				const response = await sdk.users.update(currentUserID, { OnboardingState: newState });
				const updatedState = response.OnboardingState;

				if (this.currentUserObjectFromDB) {
					this.currentUserObjectFromDB.OnboardingState = {
						...this.currentUserObjectFromDB.OnboardingState,
						...updatedState,
					};
					writeCachedRecord(USER_RECORD_CACHE_KEY, this.currentUserObjectFromDB);
				}
				return true;
			} catch (error) {
				monitoringCaptureError(error, "Sync onboarding state with DB");
				return false;
			}
		},
		async syncUserProfileWithRemote(profile: {
			Role?: string | null;
			Industry?: string | null;
			NewsletterOptIn?: boolean;
		}): Promise<boolean> {
			try {
				const currentUserID = await this.getCurrentUserID();
				if (!currentUserID) {
					throw new Error("User ID not found");
				}
				await sdk.users.update(currentUserID, profile);

				// The write is last-wins per key, so the request body is what the
				// server now holds. Keys the caller omitted stay untouched.
				if (this.currentUserObjectFromDB) {
					Object.assign(this.currentUserObjectFromDB, profile);
				}
				return true;
			} catch (error) {
				monitoringCaptureError(error, "Sync user profile (role/industry) with DB");
				return false;
			}
		},
		async syncNewsletterOptInWithRemote(optIn: boolean): Promise<boolean> {
			try {
				const currentUserID = await this.getCurrentUserID();
				if (!currentUserID) {
					throw new Error("User ID not found");
				}
				await sdk.users.update(currentUserID, {
					NewsletterOptIn: optIn,
				} as unknown as Parameters<typeof sdk.users.update>[1]);
				if (this.currentUserObjectFromDB) {
					this.currentUserObjectFromDB.NewsletterOptIn = optIn;
				}
				return true;
			} catch (error) {
				monitoringCaptureError(error, "Sync newsletter opt-in with DB");
				return false;
			}
		},
		async startFreeTrial() {
			try {
				const response = await sdk.subscriptions.update({ Tier: "ProTier" }, { freeTrialOption: true });
				this.currentUserSubscriptions = response.subscription;
				writeCachedRecord(SUBSCRIPTION_CACHE_KEY, response.subscription);
			} catch (error) {
				monitoringCaptureError(error, "Start Free Trial");
				console.log("Error starting free trial :>> ", error);
				throw error;
			}
		},

		async fetchCurrentUserActivityLogs(
			options: {
				startTime?: number;
				endTime?: number;
				limit?: number;
			} = {},
		) {
			this.activityLogsLoading = true;
			this.activityLogsError = null;

			try {
				const currentUserID = await this.getCurrentUserID();
				if (!currentUserID) {
					throw new Error("User ID not found");
				}

				const response = await sdk.users.getLogs(currentUserID, options);
				this.currentUserActivityLogs = response;
				return this.currentUserActivityLogs;
			} catch (error) {
				this.activityLogsError = error instanceof Error ? error.message : "Unknown error occurred";
				this.currentUserActivityLogs = undefined;

				if (error instanceof SdkError) {
					// We do not send 404 errors to sentry to reduce noise
					if (error.code !== ERROR_CODES.API_NOT_FOUND) {
						monitoringCaptureError(error, "Fetch user activity logs");
					}
					return null;
				}

				monitoringCaptureError(error, "Fetch user activity logs");
				return null;
			} finally {
				this.activityLogsLoading = false;
			}
		},
	},
});
